import api from "./api";

/**
 * Convert a base64url-encoded VAPID public key into the Uint8Array
 * expected by ``pushManager.subscribe({ applicationServerKey })``.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function currentPermission() {
  return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
}

export async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration("/sw.js");
  if (existing) {
    // Ask the browser to check for an updated /sw.js immediately. Cheap, idempotent.
    try { existing.update(); } catch (_) { /* noop */ }
    return existing;
  }
  return navigator.serviceWorker.register("/sw.js", {
    scope: "/",
    updateViaCache: "none",
  });
}

export async function getExistingSubscription() {
  const reg = await getServiceWorkerRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Ask for permission (if needed), register SW, create a push subscription,
 * and persist it on the backend. Returns the ``PushSubscription`` instance.
 */
export async function enablePushNotifications() {
  if (!isPushSupported()) {
    throw new Error("Push notifications are not supported in this browser.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }
  return subscribeAndPersist();
}

/**
 * Recover a missing/stale subscription when permission is already granted
 * but the device has lost its push subscription (common on Samsung after
 * an FCM token reset, an aggressive battery-saver kill, or after the
 * backend pruned a 404/410 endpoint). Does NOT prompt — only call this
 * when ``Notification.permission === "granted"``.
 */
export async function reconnectPushNotifications() {
  if (!isPushSupported()) {
    throw new Error("Push notifications are not supported in this browser.");
  }
  if (currentPermission() !== "granted") {
    throw new Error("Notification permission has not been granted.");
  }
  return subscribeAndPersist({ recreate: true });
}

/**
 * Shared subscribe + persist routine used by both the first-time enable
 * path and the recovery path. Waits for the service worker to be ready
 * (TWAs sometimes need a beat after launch before the SW is controlling
 * the page), reuses an existing PushSubscription if one is already
 * present, otherwise creates a fresh one, and POSTs it to the backend.
 */
async function subscribeAndPersist({ recreate = false } = {}) {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not available.");
  }
  // serviceWorker.ready resolves once a SW is active and controlling the
  // page. We register first so the resolution can't hang forever on a
  // brand-new install.
  await getServiceWorkerRegistration();
  const reg = await navigator.serviceWorker.ready;
  if (!reg) throw new Error("Service worker is not ready.");

  let sub = await reg.pushManager.getSubscription();
  if (sub && recreate) {
    // Throw the stale local subscription away so FCM/Mozilla mints a new
    // endpoint. The backend will accept the new one via /push/subscribe.
    try { await sub.unsubscribe(); } catch { /* best-effort */ }
    sub = null;
  }

  if (!sub) {
    const { data } = await api.get("/push/public-key");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    expirationTime: json.expirationTime,
  });
  return sub;
}

/**
 * Send a self-test push to every active subscription of the current user.
 * Returns {ok, subscriptions, sent, failed, pruned}.
 */
export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}

export async function disablePushNotifications() {
  const sub = await getExistingSubscription();
  if (!sub) return { ok: true, removed: 0 };
  const endpoint = sub.endpoint;
  try {
    await api.delete(`/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`);
  } catch {
    /* still try to unsubscribe on the browser side */
  }
  await sub.unsubscribe();
  return { ok: true, removed: 1 };
}
